import { Receipt, CheckCircle, XCircle, Trash } from '@phosphor-icons/react';
import { statusBadgeClass, statusLabel } from './AdminUI';

const Row = ({ label, value }) => (
  value ? (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-xs uppercase font-semibold text-gray-500">{label}</div>
      <div className="col-span-2 text-gray-900 font-medium break-words">{value}</div>
    </div>
  ) : null
);

const RegistrationRoster = ({ players, substitutes }) => {
  if (!players || players.length === 0) return null;
  const subs = (substitutes || []).filter(Boolean);
  return (
    <div className="pt-2">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Team Roster</div>
      <ol className="list-decimal list-inside space-y-1 text-gray-800">
        {players.map((p, idx) => (
          <li key={`player-${idx}-${p}`}>
            <span className="font-medium">{p}</span>
            {idx === 0 && <span className="ml-2 text-xs text-[#F97316] font-semibold">Captain</span>}
          </li>
        ))}
      </ol>
      {subs.length > 0 && (
        <>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-3 mb-2">Substitutes</div>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            {subs.map((s, idx) => <li key={`sub-${idx}-${s}`}>{s}</li>)}
          </ul>
        </>
      )}
    </div>
  );
};

/**
 * Reusable read-only registration detail view (heading + fields + roster + action buttons).
 * onAction(type: 'approve' | 'reject' | 'delete') is called by action buttons — parent decides what to do.
 * `actions` controls which buttons are rendered: subset of ['approve', 'reject', 'delete'].
 */
const RegistrationDetailModal = ({ detail, onClose, onOpenScreenshot, onAction, actions = ['approve', 'reject', 'delete'] }) => {
  if (!detail) return null;

  const canModerate = detail.status === 'pending_verification' || detail.status === 'form_submitted';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl my-6" data-testid="admin-registration-detail">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold">{detail.tournament_name}</p>
            <h2 className="text-xl font-bold text-gray-900">{detail.team_name || detail.player_name || detail.user_name}</h2>
            <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase ${statusBadgeClass(detail.status)}`}>
              {statusLabel(detail.status)}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="space-y-3 text-sm">
          {detail.team_name ? (
            <>
              <Row label="Captain" value={detail.captain_name} />
              <Row label="Captain Email" value={detail.captain_email} />
            </>
          ) : (
            <>
              <Row label="Player Name" value={detail.player_name} />
              <Row label="Email" value={detail.email || detail.user_email} />
            </>
          )}
          <Row label="Phone" value={detail.phone} />
          <Row label="City" value={detail.city} />
          <Row label="Age Category" value={detail.age_category} />
          {detail.created_at && <Row label="Registration Date" value={new Date(detail.created_at).toLocaleString('en-IN')} />}
          {typeof detail.registration_fee_at_reg === 'number' && (
            <Row label="Fee at registration" value={detail.registration_fee_at_reg > 0 ? `₹${detail.registration_fee_at_reg}` : 'Free'} />
          )}

          <RegistrationRoster players={detail.players} substitutes={detail.substitutes} />

          {detail.payment_screenshot_url && (
            <div className="pt-2">
              <button
                onClick={() => onOpenScreenshot(detail.payment_screenshot_url)}
                className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-xs font-semibold"
                data-testid="admin-view-payment-screenshot"
              >
                <Receipt size={14} weight="bold" /> View Payment Screenshot
              </button>
            </div>
          )}
        </div>

        {canModerate && (
          <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-100">
            {actions.includes('approve') && (
              <button
                onClick={() => onAction('approve', detail)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-2.5 rounded-lg text-sm font-semibold"
              >
                <CheckCircle size={16} weight="bold" /> Approve
              </button>
            )}
            {actions.includes('reject') && (
              <button
                onClick={() => onAction('reject', detail)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-2.5 rounded-lg text-sm font-semibold"
              >
                <XCircle size={16} weight="bold" /> Reject
              </button>
            )}
          </div>
        )}
        {actions.includes('delete') && (
          <div className={`${canModerate ? '' : 'mt-6 pt-4 border-t border-gray-100'} mt-2`}>
            <button
              onClick={() => onAction('delete', detail)}
              className="w-full inline-flex items-center justify-center gap-1.5 bg-white border border-red-200 hover:bg-red-50 text-red-700 px-3 py-2.5 rounded-lg text-sm font-semibold"
            >
              <Trash size={16} weight="bold" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationDetailModal;
